import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import SupporterBadge from '../components/SupporterBadge';
import SupporterCheckout from '../components/SupporterCheckout';

import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import { usersApi, UserUpdateRequest, PasswordUpdateRequest } from '../api/users';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  UserIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { addError } = useError();
  const [isLoading, setIsLoading] = useState(true);

  // Profile form state
  const [profileForm, setProfileForm] = useState<UserUpdateRequest>({});
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState<PasswordUpdateRequest>({
    current_password: '',
    new_password: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await usersApi.getProfile();
      setProfileForm({
        display_name: response.data.display_name,
        username: response.data.username || '',
        location: response.data.location || '',
        connection_type: response.data.connection_type || '',
        location_visible: response.data.location_visible,
        online_status_visible: response.data.online_status_visible,
        sharing_enabled: response.data.sharing_enabled,
        max_concurrent_connections: response.data.max_concurrent_connections,
        bandwidth_limit_mbps: response.data.bandwidth_limit_mbps || undefined,
        daily_data_limit_gb: response.data.daily_data_limit_gb || undefined,
      });
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to fetch profile');
      addError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsUpdatingProfile(true);
      await usersApi.updateProfile(profileForm);
      toast.success('Profile updated successfully');
      await refreshUser();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to update profile');
      addError(apiError.message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const updatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.new_password !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      await usersApi.updatePassword(passwordForm);
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '' });
      setConfirmPassword('');
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to update password');
      addError(apiError.message);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm account deletion');
      return;
    }

    try {
      setIsDeletingAccount(true);
      await usersApi.deleteAccount();
      toast.success('Account deleted successfully');
      // User will be redirected to login by auth context
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to delete account');
      addError(apiError.message);
      setIsDeletingAccount(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center mr-4 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Profile & Settings</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your account and preferences</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                {user?.display_name}
              </span>
              {user?.is_supporter && <SupporterBadge />}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">

        {/* Supporter checkout (hidden unless billing is enabled) */}
        <SupporterCheckout />

        {/* Profile Information */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <UserIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Profile Information
              </h3>
            </div>

            <form onSubmit={updateProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileForm.display_name || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, display_name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Username
                  </label>
                  <input
                    type="text"
                    value={profileForm.username || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileForm.location || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Connection Type
                  </label>
                  <select
                    value={profileForm.connection_type || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, connection_type: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">Select connection type</option>
                    <option value="FIBER">Fiber</option>
                    <option value="CABLE">Cable</option>
                    <option value="DSL">DSL</option>
                    <option value="MOBILE">Mobile</option>
                    <option value="SATELLITE">Satellite</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              {/* Privacy Settings */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Privacy Settings</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Location Visible</div>
                      <div className="text-sm text-gray-500">Allow friends to see your location</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={profileForm.location_visible || false}
                      onChange={(e) => setProfileForm({ ...profileForm, location_visible: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Online Status Visible</div>
                      <div className="text-sm text-gray-500">Show when you were last seen</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={profileForm.online_status_visible || false}
                      onChange={(e) => setProfileForm({ ...profileForm, online_status_visible: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Sharing Enabled</div>
                      <div className="text-sm text-gray-500">Allow friends to connect to your VPN</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={profileForm.sharing_enabled || false}
                      onChange={(e) => setProfileForm({ ...profileForm, sharing_enabled: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Sharing Limits */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Sharing Limits</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Max Concurrent Connections
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={profileForm.max_concurrent_connections || 5}
                      onChange={(e) => setProfileForm({ ...profileForm, max_concurrent_connections: parseInt(e.target.value) })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Bandwidth Limit (Mbps)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={profileForm.bandwidth_limit_mbps || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, bandwidth_limit_mbps: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="No limit"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Daily Data Limit (GB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={profileForm.daily_data_limit_gb || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, daily_data_limit_gb: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="No limit"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isUpdatingProfile}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Password Security */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Password & Security
              </h3>
            </div>

            <form onSubmit={updatePassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="block w-full pr-10 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCurrentPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="block w-full pr-10 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                <PasswordStrengthIndicator password={passwordForm.new_password} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isUpdatingPassword}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Danger Zone
              </h3>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Delete Account
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      Once you delete your account, there is no going back. This will permanently delete your account and remove your data from our servers.
                    </p>
                  </div>
                  <div className="mt-4">
                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="bg-red-600 border border-transparent rounded-md py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete Account
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-red-700">
                            Type "DELETE" to confirm:
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="mt-1 block w-full border-red-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            placeholder="DELETE"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={deleteAccount}
                            disabled={isDeletingAccount || deleteConfirmText !== 'DELETE'}
                            className="bg-red-600 border border-transparent rounded-md py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {isDeletingAccount ? 'Deleting...' : 'Confirm Delete'}
                          </button>
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmText('');
                            }}
                            className="bg-white border border-gray-300 rounded-md py-2 px-4 inline-flex justify-center text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </AppLayout>
  );
};

export default Profile;
